import {
  isPendingDevice,
  isTransient,
  setPendingDevice,
  unsetPendingDevice
} from '@studio/models/device';
import { toAuthentication } from '@studio/models/livekit';
import { clearDeviceDeleted, isDeviceDeleted } from './teardown.js';
import {
  describeAddDiscovery,
  describeAddFailure,
  describeBridgeDevice,
  traceCameraAdd
} from '../add-trace.js';
import type { Add } from './types.js';
import { broker, beginSourceChange, inFlightDeviceAdds } from '#~/agent/broker.js';
import { createToken } from '#~/api/livekit/index.js';
import { StudioError } from '#~/error/index.js';
import { jsToPbControl } from '#~/protobuf/index.js';
import { Protocol } from '@studio/models/video';
import {
  markDeviceElegableForAutoRestore,
  autoRestorePipelineForDeviceIfNecessaryFromURX
} from '#~/mediadevices/auto-restore.js';
// ENG-3716: bind the pipeline-add connect to the waiting-for-video-track state
// machine so a subscribe that never arrives surfaces a failure instead of an
// unbounded "Connecting...".
import { beginVideoTrackWait, clearVideoTrackWait } from '#~/livekit/track-wait.js';

/**
 * Adds a device on the specified agent.
 *
 * @param roomId - Room ID.
 * @param agentId - Agent ID.
 * @param device - Device descriptor.
 * @param token - The token.
 * @param options - Device options.
 */
export const add: Add = async (roomId, agentId, device, token, options) => {
  options?.onStage?.('pre-mutation');
  const proxy = broker.acquire(agentId);
  const started = Date.now();
  let stage = 'acquire-proxy';
  const trace = (event: string, details: Record<string, unknown> = {}) => {
    if (device.control?.config?.protocol !== 'onvif') return;
    traceCameraAdd(event, {
      deviceId: device.id,
      requestedAgentId: agentId,
      proxyId: proxy?.id,
      embedded: proxy?.embedded,
      elapsedMs: Date.now() - started,
      stage,
      bridge: describeBridgeDevice(proxy?.state, device.id),
      configuration: describeAddDiscovery({ control: device.control.config }),
      ...details
    });
  };
  trace('command.onvif-start');

  if (!proxy) {
    trace('command.proxy-missing');
    throw new StudioError(`Unable to add device: ${device.id}`, { cause: new Error('NQ-961') });
  }

  // Duplicate guards before createToken/auto-restore so concurrent callers
  // cannot both pass, then both mark pending. force keeps the existing bypass.
  // See knowledge: addDevice-single-flight-pattern (ai.knowledge/studio.client).
  let claimed = false;
  if (!options?.force) {
    const reason = inFlightDeviceAdds.has(device.id)
      ? 'in-flight'
      : isPendingDevice(device.id)
        ? 'pending'
        : proxy.state.control?.device?.[device.id]
          ? 'present'
          : undefined;
    if (reason) {
      trace('command.duplicate-skipped');
      return { kind: 'skipped', reason };
    }

    inFlightDeviceAdds.add(device.id);
    claimed = true;
  }

  // ENG-4477: a device id being (re)added must not carry a stale deletion
  // tombstone, or the settings-mutation guards would silently drop its
  // legitimate mutations. See teardown.ts.
  clearDeviceDeleted(device.id);

  const change = beginSourceChange(proxy, device.id, false);
  // force bypasses duplicate checks but still marks pending (pre-existing behavior).
  setPendingDevice({
    id: device.id,
    name: device.name,
    embedded: proxy.embedded,
    agentId: proxy.id
  });

  let success = false;
  const requireCurrent = (): void => {
    if (!change.current()) throw new DOMException('Source owner retired', 'AbortError');
  };
  // Only transient previews opt into cancellation; persistent Add/save semantics are unchanged.
  const previewSuperseded = (): boolean => {
    if (isTransient(device.id) && options?.isPreviewCurrent?.() === false) {
      unsetPendingDevice(device.id);
      return true;
    }
    return false;
  };

  try {
    if (previewSuperseded()) return { kind: 'superseded' };
    // Control-only previews publish no media; toUrx discards LiveKit auth for this shape.
    const controlOnlyPreview =
      isTransient(device.id) && !!device.control && !device.video && !device.audio;
    let liveKit = options?.liveKit;
    if (!controlOnlyPreview) {
      stage = 'create-token';
      const { token: livekitToken, endpoint } = await createToken(
        device.id,
        proxy.roomId,
        token,
        device.name
      );
      liveKit ??= toAuthentication(roomId, endpoint, livekitToken);
    }

    requireCurrent();
    if (previewSuperseded()) return { kind: 'superseded' };

    stage = 'serialize-device';
    // Materialize protobuf device.
    const pbControl = jsToPbControl(device, { liveKit });

    if (!pbControl) {
      throw new Error('CD-712');
    }

    const audioConfig = device.audio?.config;

    // Narrow union: only LocalConfig has `path`; `in` check avoids fragile @ts-ignore
    const path =
      device.video?.config?.protocol === Protocol.Local
        ? device.video?.config?.path
        : audioConfig && 'path' in audioConfig
          ? audioConfig.path
          : undefined;

    // ENG-3716: bind persisted room-tile connects to the video-track wait.
    // Transient previews have their own overlay and can return superseded
    // after this point — starting a 15s wait on a preview id would paint
    // Camera Unreachable on a device that is being torn down.
    // Per settle-every-terminal-branch (ai.knowledge/global): every exit after
    // begin must clear the wait; success leaves it for subscribe/timeout.
    const trackWaitStarted = Boolean(device.video) && !isTransient(device.id);
    if (trackWaitStarted) {
      beginVideoTrackWait(device.id);
    }
    const abandonTrackWait = (): void => {
      if (trackWaitStarted) clearVideoTrackWait(device.id);
    };

    if (pbControl.device?.[device.id]?.urx?.includes('browsermedia://')) {
      await markDeviceElegableForAutoRestore('video', path!, device.id, false);
      if (previewSuperseded()) {
        abandonTrackWait();
        return { kind: 'superseded' };
      }
      await autoRestorePipelineForDeviceIfNecessaryFromURX(
        pbControl.device?.[device.id]?.urx!,
        agentId,
        device.id
      );
    }

    requireCurrent();
    if (previewSuperseded()) {
      abandonTrackWait();
      return { kind: 'superseded' };
    }
    // A display deadline or closed dialog cannot cancel IO, but can stop the next write.
    if (options?.shouldContinue?.() === false) {
      unsetPendingDevice(device.id);
      abandonTrackWait();
      return { kind: 'superseded' };
    }
    // A teardown that began while add awaited must prevent a late source resurrection.
    // Keep the superseded outcome and track-wait cleanup used by the other add fences.
    if (isDeviceDeleted(device.id)) {
      trace('command.deleted-during-add');
      unsetPendingDevice(device.id);
      abandonTrackWait();
      return { kind: 'superseded' };
    }
    options?.onStage?.('mutation-sent');
    stage = 'mutate-bridge';
    trace('command.mutation-start');
    await proxy.mutate(
      {
        control: pbControl
      },
      {},
      true
    );
    trace('command.mutation-acknowledged');

    requireCurrent();
    success = true;
    options?.onStage?.('mutation-acknowledged');

    // Only a resolved permanent save proves persistence.
    if (!isTransient(device.id)) {
      options?.onStage?.('save-pending');
      stage = 'save-bridge';
      await proxy.save(token);
      trace('command.save-complete');
      options?.onStage?.('saved');
      return { kind: 'saved', deviceId: device.id };
    }
    return { kind: 'transient-added', deviceId: device.id };
  } catch (cause) {
    trace('command.failed', { failure: describeAddFailure(cause) });
    unsetPendingDevice(device.id);
    // ENG-3716 / per rule settle-every-terminal-branch (ai.knowledge/global):
    // the add itself failed and already surfaces its own error — do not leave
    // a pending wait that would later report a bogus track timeout.
    clearVideoTrackWait(device.id);

    throw new StudioError(`Unable to add device: ${device.id}`, { cause });
  } finally {
    change.finish(success);
    // Per rule: settle-every-terminal-branch (ai.knowledge/global) — only release what we claimed.
    if (claimed) {
      inFlightDeviceAdds.delete(device.id);
    }
  }
};
